import React, { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { ClipLoader } from "react-spinners";
import { IoClose } from "react-icons/io5";
import uploadIcon from "../../assets/icons/uploadIcon.svg";
import music from "../../assets/icons/music.png";
import { twMerge } from "tailwind-merge";
import * as mm from "music-metadata-browser";
import { processImageFile } from "../../utils/processImageFile";
import { FormHelperText, formStyles } from "./FormUtils";

const FileUpload = ({
  id,
  label = "Select your file or drag and drop here",
  onFileUpload,
  uploadFile,
  onRemove,
  onMetaExtracted,
  isRequired = false,
  labelStyle = "",
  maxSize = 20,
  type = "image",
  allowRemove = true,
  uploadText = "",
  dragText = "",
  error: externalError,
  showErrorBelow = true,
  expectedTitle = "",
  defaultValue = null,
  containerStyle = "",
  onFocus,
  showPreview = false,

  minWidth = 300,
  minHeight = 300,
  targetWidth = 500,
  targetHeight = 500,
  aspectRatio = 1,
  quality = 0.85,
}) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState("");
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // Fixed useEffect to properly handle defaultValue changes including null values
  useEffect(() => {
    if (defaultValue !== file) {
      setFile(defaultValue);

      if (!defaultValue) {
        setError("");
        setUploadProgress(0);
      }
    }
  }, [defaultValue]);

  const getAcceptedFileTypes = () => {
    switch (type) {
      case "image":
        return { "image/jpeg": [".jpeg", ".jpg"], "image/png": [".png"] };
      case "audio":
        return {
          "audio/mpeg": [".mp3"],
          "audio/wav": [".wav"],
          "audio/flac": [".flac"],
        };
      default:
        return "*/*";
    }
  };

  const handleFocus = useCallback(() => {
    // Clear internal error
    setError("");
    
    // Call the external onFocus handler if provided
    if (onFocus) {
      onFocus();
    }
  }, [onFocus]);

  const onDrop = useCallback(
    async (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        const rejection = rejectedFiles[0];
        const code = rejection.errors[0]?.code;
        if (code === "file-invalid-type") {
          setError(
            `Invalid file type. Allowed: ${Object.values(getAcceptedFileTypes())
              .flat()
              .join(", ")}`
          );
        } else if (code === "file-too-large") {
          setError(`File exceeds ${maxSize}MB`);
        }
        return;
      }

      setError("");
      const uploadedFile = acceptedFiles[0];

      if (uploadedFile.size > maxSize * 1024 * 1024) {
        setError(`File exceeds ${maxSize}MB`);
        return;
      }

      setLoading(true);
      setUploadProgress(0);

      try {
        let fileToUse = uploadedFile;

        if (type === "image") {
          fileToUse = await processImageFile(uploadedFile, {
            minWidth: minWidth,
            minHeight: minHeight,
            targetWidth: targetWidth,
            targetHeight: targetHeight,
            aspectRatio: aspectRatio,
            quality: quality,
          });
        }

        if (uploadFile) {
          const uploadedResult = await uploadFile(
            fileToUse,
            (progressEvent) => {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setUploadProgress(percentCompleted);
            }
          );
          setFile(uploadedResult);
          onFileUpload(uploadedResult);
        } else {
          setFile(fileToUse);
          onFileUpload(fileToUse);
          setUploadProgress(100);
        }

        if (type === "audio") {
          const metadata = await mm.parseBlob(fileToUse);
          const extractedTitle =
            metadata.common.title?.trim() ||
            fileToUse.name.replace(/\.[^/.]+$/, "").trim();
          const rawTitle = extractedTitle.replace(/\s*\(.*?\)/g, "").trim();

          if (
            expectedTitle &&
            rawTitle.toLowerCase() !== expectedTitle.toLowerCase()
          ) {
            setError(
              `Track title mismatch. Expected: "${expectedTitle}", Found: "${extractedTitle}"`
            );
            setFile(null);
            onFileUpload(null);
            onMetaExtracted && onMetaExtracted(null);
            setLoading(false);
            setUploadProgress(0);
            return;
          }

          const extracted = {
            title: extractedTitle,
            artist: metadata.common.artist,
            bpm: metadata.common.bpm || "",
            genre: metadata.common.genre?.[0]
              ? metadata.common.genre[0] +
                (metadata.common.genre[1]
                  ? " / " + metadata.common.genre[1]
                  : "")
              : "",
            year: metadata.common.year,
            key: metadata.common.key,
            comments:
              metadata.common.comment?.[0] || metadata.common.comments?.[0],
          };
          console.log("Extracted metadata:", extracted);

          onMetaExtracted && onMetaExtracted(extracted);
        }
      } catch (err) {
        setError(err.message || "Upload failed");
        setFile(null);
        onFileUpload(null);
        onMetaExtracted && onMetaExtracted(null);
        setUploadProgress(0);
      } finally {
        setLoading(false);
      }
    },
    [maxSize, onFileUpload, type, onMetaExtracted, uploadFile, expectedTitle]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: getAcceptedFileTypes(),
    multiple: false,
    maxSize: maxSize * 1024 * 1024,
  });

  const handleRemove = (e) => {
    if (onRemove) {
      onRemove();
    }
    e.stopPropagation();
    setFile(null);
    onFileUpload(null);
    setError("");
    setUploadProgress(0);
    onMetaExtracted && onMetaExtracted(null);
  };

  const handleImageClick = (e) => {
    if (showPreview && type === "image" && file) {
      e.stopPropagation();
      setShowPreviewModal(true);
    }
  };

  const closePreview = () => {
    setShowPreviewModal(false);
  };

  const getImageSrc = () => {
    return typeof file === "string"
      ? file
      : file instanceof File
      ? URL.createObjectURL(file)
      : file.url;
  };

  const getBorderClasses = () => {
    if (error || externalError) return "border border-red-500 bg-[#45444433]";
    return "border border-[#808080] bg-[#45444433]";
  };

  return (
    <div
      className={twMerge(
        "flex flex-col  w-full text-[#808080]",
        containerStyle
      )}
    >
      {label && (
        <div className="flex items-center gap-2">
          <label
            htmlFor={id}
            className={twMerge(
              "text-[14px] text-[#E3E3E3] font-medium leading-[22px]",
              formStyles.dropDownlabel,
              labelStyle
            )}
          >
            {label} {isRequired && <span className="text-light-red">*</span>}
          </label>
          {!showErrorBelow && (error || externalError) && (
            <span className="text-[#C00707] text-[12px] leading-[22px] font-normal font-['Public_Sans'] ml-[1px] mb-1 whitespace-nowrap">
              {error || externalError}
            </span>
          )}
        </div>
      )}
      <div
        {...getRootProps()}
        className={twMerge(
          "w-full rounded-md cursor-pointer transition-colors h-[45px]",
          getBorderClasses()
        )}
        onFocus={handleFocus}
      >
        <input {...getInputProps()} onFocus={handleFocus} />
        <div className="flex flex-col w-full gap-2">
          {loading ? (
            <ClipLoader size={30} color="#3498db" />
          ) : file ? (
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center space-x-3">
                {type === "audio" ? (
                  <img
                    src={music}
                    alt="audio"
                    className="w-12 h-[44px] object-cover rounded"
                  />
                ) : (
                  <img
                    src={getImageSrc()}
                    alt="preview"
                    className={twMerge(
                      "w-12 h-[44px] object-cover rounded",
                      showPreview && "cursor-pointer hover:opacity-80 transition-opacity"
                    )}
                    onClick={handleImageClick}
                  />
                )}
                <div className="text-[#197AD0] font-medium text-[14px] sm:text-[12px] md:text-[10px] break-words">
                  {typeof file === "string"
                    ? type === "audio"
                      ? "Audio File"
                      : "Image File"
                    : file.name}
                </div>
              </div>
              {allowRemove && (
                <button
                  type="button"
                  onClick={handleRemove}
                  className="text-[#808080] hover:text-red-400 pr-2"
                >
                  <IoClose size={22} />
                </button>
              )}
            </div>
          ) : (
            <div className="flex justify-between border-[1px] rounded-md border-[#191919] text-[18px] font-Public_Sans items-center w-full h-[42px] ">
              <span className="pl-2 text-[14px] font-Public_Sans text-[#808080] font-normal">
                {dragText ||
                  uploadText ||
                  "Select your file or drag and drop here"}
              </span>
              <img
                src={uploadIcon}
                alt="upload"
                className="w-[50.67px] h-[42px] object-contain block border-[#191919] rounded-lg"
              />
            </div>
          )}

          {loading && (
            <>
              <div className="w-full bg-gray-700 rounded h-2 mt-2">
                <div
                  className="bg-blue-500 h-2 rounded"
                  style={{
                    width: `${uploadProgress}%`,
                    transition: "width 0.3s ease",
                  }}
                />
              </div>
              <span className="text-right text-xs text-blue-400 pt-2">
                {uploadProgress}%
              </span>
            </>
          )}
        </div>
      </div>
      
      {/* Preview Modal */}
      {showPreviewModal && type === "image" && file && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="relative max-w-4xl max-h-[90vh] bg-[#2a2a2a] rounded-lg p-4 pt-10">
            <button
              onClick={closePreview}
              className="absolute top-2 right-2 text-white opacity-40 hover:text-white hover:opacity-100 z-10 mb-3"
            >
              <IoClose size={28} />
            </button>
            <img
              src={getImageSrc()}
              alt="Preview"
              className="max-w-full max-h-[80vh] object-contain rounded"
            />
          </div>
        </div>
      )}
      
      {showErrorBelow && (error || externalError) && (
        <FormHelperText
          error={error || externalError}
          className={formStyles.errorContainer}
          errorClassName={formStyles.errorText}
        />
      )}
    </div>
  );
};

export default FileUpload;